import React, { useState, useEffect, memo } from 'react';
import { useApp } from '../context/AppContext';
import { logoStorageService } from '../services/storageService';
import { useUserRole } from '../hooks/useUserRole';
import { hasPageAccess, canAccessSettings } from '../utils/rolePermissions';
import { isStageEnabled, hasGarageLanesEnabled } from '../utils/enabledStages';
import { APP_VERSION } from '../config/app';

const Sidebar = memo(({ currentPage, onNavigate, onLogout, onOpenSettings, isOpen, onClose, onOpenChat }) => {
  const { garageInfo } = useApp();
  const { role, permissions } = useUserRole();
  const [logoUrl, setLogoUrl] = useState('');
  const [openSubmenus, setOpenSubmenus] = useState(new Set());

  useEffect(() => {
    // Load logo from Firestore only - no localStorage fallback
    const loadLogo = async () => {
      try {
        const url = await logoStorageService.getLogoURL();
        if (url) {
          setLogoUrl(url);
          console.log('Sidebar: Logo loaded successfully:', url);
        } else {
          // No logo found - will show default logo
          console.log('Sidebar: No logo found, using default');
        }
      } catch (error) {
        console.error('Error loading logo from Firestore:', error);
        // No fallback - use default logo
      }
    };
    loadLogo();

    // Listen for branding updates (when logo is changed in settings)
    const handleBrandingUpdate = (e) => {
      if (e.detail.logo !== undefined) {
        setLogoUrl(e.detail.logo || '');
        console.log('Sidebar: Logo updated via event:', e.detail.logo || 'default');
      }
    };
    window.addEventListener('brandingUpdated', handleBrandingUpdate);

    return () => {
      window.removeEventListener('brandingUpdated', handleBrandingUpdate);
    };
  }, []);

  const isGarageSubmenuItemVisible = (subItemId) => {
    if (subItemId === 'garage-report' || subItemId === 'parts-lookup') {
      return hasGarageLanesEnabled(garageInfo);
    }
    return true;
  };

  const navItems = [
    { id: 'overview', label: 'Dashboard', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </svg>
    )},
    { 
      id: 'garagevehicles', 
      label: 'Garage Vehicles', 
      icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1"></path>
        <polyline points="12 15 17 21 7 21 12 15"></polyline>
        <path d="M12 15V3"></path>
      </svg>
      ),
      submenu: [
        { id: 'garagevehicles', label: 'Garage Vehicles' },
        { id: 'garage-report', label: 'Garage Report' },
        { id: 'report', label: 'Report' },
        { id: 'parts-lookup', label: 'Parts Lookup' }
      ]
    },
    { id: 'calendar', label: 'Calendar', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
    )},
    { id: 'invoices', label: 'Invoices', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
      </svg>
    )},
    { id: 'customers', label: 'Customers', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    )},
    { 
      id: 'sales', 
      label: 'Sales', 
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 11l3 3L22 4"></path>
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
        </svg>
      ),
      submenu: [
        { id: 'sales', label: 'Sales Board' },
        { id: 'sales-report', label: 'Sales Report' }
      ]
    },
    { id: 'archive', label: 'Archive', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
        <circle cx="12" cy="10" r="3"></circle>
      </svg>
    )},
    { id: 'finance', label: 'Finance', icon: (
      <span className="sidebar-nav-pound-icon">£</span>
    )},
    { id: 'links', label: 'Links', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
      </svg>
    )},
    { id: 'technician-dispatch', label: 'Technicians', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        <path d="M12 8v4l2 2"></path>
      </svg>
    )},
    { id: 'payment', label: 'My Account', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
        <line x1="1" y1="10" x2="23" y2="10"></line>
      </svg>
    )}
  ];

  // Keep submenus open when navigating to their items
  useEffect(() => {
    setOpenSubmenus(prev => {
      const newOpenSubmenus = new Set(prev);
      navItems.forEach(item => {
        if (item.submenu && item.submenu.some(sub => sub.id === currentPage)) {
          newOpenSubmenus.add(item.id);
        }
      });
      return newOpenSubmenus;
    });
    // navItems is static per render; only currentPage should trigger this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Toggle submenu open/closed
  const toggleSubmenu = (itemId) => {
    setOpenSubmenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Check if submenu should be open
  const isSubmenuOpen = (item) => {
    if (!item.submenu) return false;
    // Open if explicitly toggled open OR if current page is one of the submenu items
    return openSubmenus.has(item.id) || item.submenu.some(sub => sub.id === currentPage);
  };

  return (
    <aside className={`sidebar ${isOpen ? 'mobile-open' : ''}`}>
      <button 
        className="sidebar-close-btn"
        onClick={onClose}
        aria-label="Close menu"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="sidebar-logo-image" />
          ) : (
            <svg 
              viewBox="0 0 100 120" 
              className="sidebar-logo-image"
              style={{ background: '#ffffff', borderRadius: '8px', padding: '4px' }}
            >
              {/* Black circle with white center (donut shape) */}
              <circle cx="50" cy="40" r="30" fill="#000000" />
              <circle cx="50" cy="40" r="18" fill="#ffffff" />
              {/* Cyan rectangle below */}
              <rect x="30" y="80" width="40" height="12" fill="#00bcd4" rx="2" />
            </svg>
          )}
        </div>
        {!logoUrl && (
          <div className="sidebar-brand-tagline" aria-hidden="true">Plutoo Dealer</div>
        )}
        <div className="sidebar-info">
          <div className="sidebar-info-item">
            {/* Address is pulled from Settings/Company Info via AppContext */}
            <div className="sidebar-info-value">{garageInfo?.address || '123 Motor Street, London, SW1A 1AA'}</div>
          </div>
        </div>
      </div>

      <nav className="sidebar-nav">
        {navItems
          .filter(item => {
            if (item.id === 'sales' && !isStageEnabled(garageInfo, 'Sales')) return false;
            if (item.id === 'technician-dispatch' && !hasGarageLanesEnabled(garageInfo)) return false;
            // If permissions haven't loaded yet, show all items (will be filtered once loaded)
            // But also check if role is available to make a better initial guess
            if (!permissions) {
              if (role) {
                const defaultPerms = hasPageAccess(role, null, item.id);
                if (item.submenu) {
                  return item.submenu.some(subItem =>
                    isGarageSubmenuItemVisible(subItem.id) && hasPageAccess(role, null, subItem.id)
                  );
                }
                return defaultPerms;
              }
              return true;
            }
            if (item.id === 'sales' && !isStageEnabled(garageInfo, 'Sales')) return false;
            if (item.id === 'technician-dispatch' && !hasGarageLanesEnabled(garageInfo)) return false;
            // Filter out items based on user permissions
            // Check if user has access to the main page or any submenu page
            if (item.submenu) {
              // For items with submenu, show if user has access to any submenu item
              return item.submenu.some(subItem =>
                isGarageSubmenuItemVisible(subItem.id) && hasPageAccess(role, permissions, subItem.id)
              );
            } else {
              // For regular items, check if user has access
              return hasPageAccess(role, permissions, item.id);
            }
          })
          .map(item => (
            <div key={item.id}>
              <button
                type="button"
                className={`sidebar-nav-item ${currentPage === item.id || (item.submenu && item.submenu.some(sub => sub.id === currentPage)) ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (item.submenu) {
                    // Toggle submenu if it has one
                    const wasOpen = isSubmenuOpen(item);
                    toggleSubmenu(item.id);
                    // If submenu was closed and is now opening, navigate to first accessible submenu item
                    if (!wasOpen) {
                      const accessibleSubItem = item.submenu.find(sub => hasPageAccess(role, permissions, sub.id));
                      if (accessibleSubItem) {
                        onNavigate(accessibleSubItem.id);
                      }
                    }
                  } else {
                    onNavigate(item.id);
                  }
                }}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.submenu && (
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    style={{ 
                      width: '16px', 
                      height: '16px', 
                      marginLeft: 'auto',
                      transition: 'transform 0.2s',
                      transform: isSubmenuOpen(item) ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                )}
              </button>
              {item.submenu && (
                <div 
                  className={`sidebar-submenu ${isSubmenuOpen(item) ? 'submenu-open' : ''}`}
                >
                  {item.submenu
                    .filter(subItem => isGarageSubmenuItemVisible(subItem.id) && hasPageAccess(role, permissions, subItem.id))
                    .map(subItem => (
                      <button
                        key={subItem.id}
                        type="button"
                        className={`sidebar-nav-item sidebar-submenu-item ${currentPage === subItem.id ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onNavigate(subItem.id);
                        }}
                        style={{ paddingLeft: '48px' }}
                      >
                        <span>{subItem.label}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          ))}
        <div key="chat-support">
          <button
            type="button"
            className="sidebar-nav-item"
            onClick={() => {
              if (onOpenChat) onOpenChat();
              if (onClose) onClose();
            }}
            aria-label="Chat Support"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              <circle cx="9" cy="10" r="1"></circle>
              <circle cx="15" cy="10" r="1"></circle>
              <path d="M9 14s1.5 2 3 2 3-2 3-2"></path>
            </svg>
            <span>Chat Support</span>
          </button>
        </div>
        {canAccessSettings(role, permissions) && (
          <div key="settings">
            <button type="button" className="sidebar-nav-item" onClick={onOpenSettings} aria-label="Settings">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3m15.364 6.364l-4.243-4.243m-4.242 0L5.636 17.364m12.728 0l-4.243-4.243m-4.242 0L5.636 6.636"></path>
              </svg>
              <span>Settings</span>
            </button>
          </div>
        )}
      </nav>
      <div className="sidebar-bottom">
        <div className="sidebar-logout-wrap">
          <button type="button" className="sidebar-nav-item sidebar-footer-logout" onClick={onLogout}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <span>Logout</span>
          </button>
          <span className="sidebar-version">Version {APP_VERSION}</span>
        </div>
      </div>
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;

